// @flow
import type { IntlShape, MessageDescriptor } from 'react-intl';

import React from 'react';
import { injectIntl, FormattedMessage } from 'react-intl';
import { Input } from 'dibs-elements/exports/Input';
import { BasicSelect } from 'dibs-elements/exports/BasicSelect';
import { Button } from 'dibs-elements/exports/Button';
import { BarHeader } from 'dibs-elements/exports/BarHeader';
import { Tooltip } from 'dibs-elements/exports/Tooltip';
import styles from './styles/DealerLoginForm.scss';
import formCopy from '../../../../../copy/loginCopy.es';
import QuestionMarkEmpty from 'dibs-vg/dist/react/circle-question-mark-empty';
import QuestionMarkFilled from 'dibs-vg/dist/react/circle-question-mark-filled';
import classNames from 'classnames';

type LOGIN_VIEW_TYPES = 'login' | 'resetPasswordEmail' | 'resetPassword';

type ExternalProps = {
    intl: IntlShape,
};

type LoginParentProps = {
    action: string,
    errors: {
        email: MessageDescriptor,
        password: MessageDescriptor,
        password1: MessageDescriptor,
        vertical: MessageDescriptor,
    },
    formType: LOGIN_VIEW_TYPES,
    handleChange: (e: mixed, field: string) => mixed,
    handleFieldBlur: (e: mixed, field: string, formType?: string) => mixed,
    handleSubmit: (e: mixed, formType: LOGIN_VIEW_TYPES) => boolean,
    handleTooltipClick?: () => mixed,
    handleVerticalSelection: (selection: SyntheticInputEvent<HTMLSelectElement>) => void,
    resetLink?: string,
    resetVertical: ?string,
    tooltipIsVisible: boolean,
};

type LoginProps = LoginParentProps & ExternalProps;

/*
 ** Making this a class so that this component behaves nicely with flow-typed.
 ** Right now there's an issue with functional components wrapped with injectIntl
 ** flow's $Diff produces an empty type. Making the component a class solves this.
 ** More details: https://github.com/flowtype/flow-typed/issues/1529
 */
class LoginForm extends React.Component<LoginProps> {
    constructor(props: LoginProps) {
        super(props);
    }
    render() {
        const {
            action,
            errors,
            formType,
            handleFieldBlur,
            handleChange,
            handleSubmit,
            handleTooltipClick,
            handleVerticalSelection,
            intl,
            resetLink,
            resetVertical,
            tooltipIsVisible,
        } = this.props;

        const options = [
            { label: 'Furniture', value: 'furniture' },
            { label: 'Jewelry', value: 'jewelry' },
            { label: 'Fashion', value: 'fashion' },
            { label: 'Fine Art', value: 'art' },
        ];

        const QuestionMark = tooltipIsVisible ? QuestionMarkFilled : QuestionMarkEmpty;

        const isLogin = formType === 'login';
        const isResetEmail = formType === 'resetPasswordEmail';
        const isPasswordReset = formType === 'resetPassword';

        const emailPasswordName = !isPasswordReset ? 'email' : 'password';
        const passwordName = !isPasswordReset ? 'password' : 'password1';

        const getIntlCopy = (copy: MessageDescriptor): string => {
            return copy.defaultMessage ? intl.formatMessage(copy) : '';
        };
        // this comes from the login pageModel template and set in twig file
        const goToEl = document.getElementById('goTo');
        // must add the hidden form element for the loginResponseHandlers to use
        const goTo = goToEl && goToEl.value ? goToEl.value : '';

        return (
            <div
                id="login"
                className={classNames('well well-white login-dealer', styles.loginWell)}
            >
                <BarHeader
                    title={intl.formatMessage(formCopy[formType].heading)}
                    subtitle={getIntlCopy(formCopy[formType].subtitle)}
                />
                <form
                    className={classNames('form-horizontal', styles.form)}
                    method="post"
                    action={action}
                    onSubmit={e => handleSubmit(e, formType)}
                >
                    <input type="hidden" name="goTo" value={goTo} />
                    <div
                        className={classNames(styles.formGroup, {
                            [styles.resetEmail]: isResetEmail,
                        })}
                    >
                        <Input
                            dataTn={`${emailPasswordName}-input`}
                            errorMessage={getIntlCopy(errors[emailPasswordName])}
                            label={intl.formatMessage(formCopy[formType].inputLabelText1)}
                            name={emailPasswordName}
                            onBlur={e => handleFieldBlur(e, emailPasswordName, formType)}
                            onChange={e => handleChange(e, emailPasswordName)}
                            type={emailPasswordName}
                        />
                    </div>
                    <div className={styles.formGroup}>
                        {!isResetEmail ? (
                            <Input
                                dataTn={`${passwordName}-input`}
                                errorMessage={getIntlCopy(errors[passwordName])}
                                label={intl.formatMessage(formCopy[formType].inputLabelText2)}
                                name={passwordName}
                                onBlur={e => handleFieldBlur(e, passwordName, formType)}
                                onChange={e => handleChange(e, passwordName)}
                                type={'password'}
                            />
                        ) : (
                            <div>
                                <div className={styles.verticalSelectionContainer}>
                                    <div className={styles.verticalSelection}>
                                        <p className={styles.fieldLabel}>
                                            <FormattedMessage
                                                id="login.reset_email_vertical_selection"
                                                defaultMessage="Selling Category"
                                            />
                                        </p>
                                        <BasicSelect
                                            dataTn={'login-vertical-drop-down'}
                                            errorMessage={getIntlCopy(errors.vertical)}
                                            name="vertical"
                                            onChange={handleVerticalSelection}
                                            options={options}
                                            placeholder={' '}
                                            size={'large'}
                                            value={resetVertical}
                                        />
                                    </div>

                                    <div className={styles.tooltip} onClick={handleTooltipClick}>
                                        <QuestionMark className={styles.questionMark} />
                                        <Tooltip
                                            isVisible={tooltipIsVisible}
                                            direction={'right'}
                                            dataTn={'login-vertical-tooltip'}
                                            type={'coachmark'}
                                        >
                                            <FormattedMessage {...formCopy[formType].tooltip} />
                                        </Tooltip>
                                    </div>
                                </div>
                                <div className={styles.mobileHint}>
                                    <FormattedMessage {...formCopy[formType].tooltip} />
                                </div>
                            </div>
                        )}
                    </div>

                    {isLogin && (
                        <a
                            className={styles.forgotPWLink}
                            href={resetLink}
                            data-tn={'forgot-pw-reset-link'}
                        >
                            <FormattedMessage {...formCopy[formType].resetPasswordLink} />
                        </a>
                    )}
                    <br />
                    <div className={styles.formSubmitButton}>
                        <Button className={styles.fullWidth} dataTn="login-submit">
                            <FormattedMessage id="login.submit" defaultMessage="SUBMIT" />
                        </Button>
                    </div>
                </form>
            </div>
        );
    }
}

// $FlowFixMe
export default injectIntl(LoginForm);
